import { useRef, useState } from "react";
import { useInspection } from "./InspectionContext";
import type { LocalPhoto } from "./InspectionContext";
import {
  Breadcrumb,
  CameraIcon,
  CloseIcon,
  UploadIcon,
  formatBytes,
} from "./shared";

export function UploadScreen() {
  const { photos, addFiles, removePhoto, submit } = useInspection();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [rejectedCount, setRejectedCount] = useState(0);

  function handleFiles(files: FileList | File[]) {
    const { rejected } = addFiles(files);
    setRejectedCount(rejected);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
  }

  const canSubmit = photos.length > 0;

  return (
    <>
      <Breadcrumb trail={["Inspections", "New"]} />
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Upload vehicle photos
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Add all return-condition photos. We'll run damage detection on
            each once you submit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="rounded-md bg-accent px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors enabled:hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-border-grey disabled:text-text-secondary"
          >
            Submit for analysis
            {canSubmit ? ` (${photos.length})` : ""}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          className={
            "rounded-card border-2 border-dashed bg-white p-10 transition-colors " +
            (dragActive
              ? "border-accent bg-surface-muted"
              : "border-border-grey hover:border-accent")
          }
        >
          <div className="flex flex-col items-center text-center">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-surface-light">
              <UploadIcon />
            </div>
            <h2 className="mt-4 text-lg font-bold">
              Drop vehicle photos here
            </h2>
            <p className="mt-1 max-w-md text-sm text-text-secondary">
              Capture all vehicle sides, and any visible damage. JPG or PNG
              only, up to 5MB each.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <button
                onClick={() => inputRef.current?.click()}
                className="rounded-md bg-accent px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-accent-hover"
              >
                Select files
              </button>
              <button
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-md border border-sixt-black bg-white px-5 py-2.5 text-sm font-semibold hover:bg-surface-light"
              >
                <CameraIcon />
                Use camera
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) handleFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </div>
            {rejectedCount > 0 && (
              <div className="mt-4 rounded-md border border-[#D32F2F]/30 bg-[#D32F2F]/5 px-3 py-2 text-xs font-semibold text-[#D32F2F]">
                {rejectedCount} file{rejectedCount > 1 ? "s" : ""} rejected —
                only JPG and PNG are accepted.
              </div>
            )}
          </div>
        </div>

        {photos.length > 0 && (
          <div className="rounded-card border border-border-grey bg-white">
            <div className="flex items-center justify-between border-b border-border-grey px-5 py-3">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-bold uppercase tracking-wider">
                  Queued
                </h3>
                <span className="rounded-full bg-surface-light px-2 py-0.5 text-xs font-semibold text-text-secondary">
                  {photos.length} photo{photos.length > 1 ? "s" : ""}
                </span>
              </div>
              <div className="text-xs text-text-secondary">
                Minimum 6 recommended
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 p-5 md:grid-cols-3">
              {photos.map((p) => (
                <QueuedTile
                  key={p.id}
                  photo={p}
                  onRemove={() => removePhoto(p.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function QueuedTile({
  photo,
  onRemove,
}: {
  photo: LocalPhoto;
  onRemove: () => void;
}) {
  return (
    <div className="group overflow-hidden rounded-card border border-border-grey bg-surface-muted">
      <div className="relative aspect-[4/3] bg-hero-charcoal">
        <img
          src={photo.previewUrl}
          alt={photo.file.name}
          className="h-full w-full object-cover"
        />
        <button
          onClick={onRemove}
          className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/70 text-white opacity-0 transition-opacity hover:bg-black group-hover:opacity-100"
          aria-label="Remove photo"
        >
          <CloseIcon />
        </button>
      </div>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">
            {photo.file.name}
          </div>
          <div className="text-xs text-text-secondary">
            {formatBytes(photo.file.size)}
          </div>
        </div>
      </div>
    </div>
  );
}
